/**
 * KOA-18737 / KOA-18738 — raw-JSON datasource types.
 *
 * A C# datasource may declare a DTO member, an action return or an action parameter as Newtonsoft
 * `JObject`, `JArray` or `JToken`: JSON whose shape the designer does not know. This is the TypeScript mirror
 * of `Kuika.Common.Json.NewtonsoftLinqTypes` (C#); the two must agree, because the stored type name reaches
 * the designer in whichever spelling the platform produced at save time — bare (`JObject`),
 * Newtonsoft-qualified (`Newtonsoft.Json.Linq.JObject`, what the compiler stores since the fix) or the
 * pre-fix datasource-namespace prefix (`Kuika.ThirdPartyApisCollection.JObject`).
 *
 * Classification is by the last dot-segment, compared with locale-independent lower-casing.
 */

export type NewtonsoftLinqType = "JObject" | "JArray" | "JToken";

/** The subset of an action parameter the raw-JSON predicates read; both DTO spellings of the type field. */
export interface IRawJsonParameterLike {
  typeName?: string | null;
  Typename?: string | null;
  isObject?: boolean;
  objectClassName?: string | null;
}

export function classifyNewtonsoftLinqType(typeName: string | null | undefined): NewtonsoftLinqType | null {
  if (typeof typeName !== "string") return null;
  const trimmed: string = typeName.trim();
  if (trimmed.length === 0) return null;
  const lastDot: number = trimmed.lastIndexOf(".");
  const simpleName: string = (lastDot < 0 ? trimmed : trimmed.substring(lastDot + 1)).toLowerCase();
  if (simpleName === "jobject") return "JObject";
  if (simpleName === "jarray") return "JArray";
  if (simpleName === "jtoken" || simpleName === "jvalue") return "JToken";
  return null;
}

/**
 * Whether an action parameter is raw JSON rather than a compiled DTO. A definition saved before the compiler
 * normalised these names carries `isObject: true` with no compiled class behind it — such a parameter has no
 * nested fields to map and must be bound as a single value. A parameter whose class name resolves to a real
 * compiled type keeps it, even when that class happens to be called `JObject`.
 */
export function isRawJsonParameter(parameter: IRawJsonParameterLike | null | undefined): boolean {
  if (!parameter) return false;
  const typeName: string | null | undefined = parameter.typeName ?? parameter.Typename;
  if (classifyNewtonsoftLinqType(typeName) === null) return false;
  const objectClassName: string | null | undefined = parameter.objectClassName;
  if (typeof objectClassName !== "string" || objectClassName.trim().length === 0) return true;
  return objectClassName.trim().toLowerCase().startsWith(NEWTONSOFT_LINQ_NAMESPACE_PREFIX);
}

const NEWTONSOFT_LINQ_NAMESPACE_PREFIX: string = "newtonsoft.json.linq.";

/**
 * The "this parameter is a nested object with its own fields" predicate the binding UI should use instead of
 * reading `isObject` directly: an object parameter that is really raw JSON is bound like a scalar.
 */
export function isComplexObjectParameter(parameter: IRawJsonParameterLike | null | undefined): boolean {
  if (!parameter || parameter.isObject !== true) return false;
  return !isRawJsonParameter(parameter);
}
